import { EXTENSION_MIMETYPE } from '@/modules/common/common/constants/CommonConstants'
import AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import SectionFactory from '@/modules/common/common/models/APP/InsuranceSectionsFactory'
import AbstractAccordion from '@/modules/common/common/models/APP/accordions/AbstractAccordion'
import BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import ExistClientRequest from '@/modules/common/hiring/models/MID/requests/ExistClientRequest'
import type ForeignHealthHiringInsured from '@/modules/foreign-health/hiring/models/APP/ForeignHealthHiringInsured'
import HealthInsurance from '@/modules/health/common/models/APP/HealthInsurance'
import AdditionalCoverageConfig from '@/modules/health/hiring/models/APP/AdditionalCoverageConfig'
import HealthHiringAnswers from '@/modules/health/hiring/models/APP/HealthHiringAnswers'
import HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
import HealthInsuranceProcessStepEnum from '@/modules/health/hiring/models/APP/HealthInsuranceProcessStepEnum'
import HealthLandingInsured from '@/modules/health/hiring/models/APP/HealthLandingInsured'
import HealthRaterAnswers from '@/modules/health/rater/models/APP/HealthRaterAnswers'
import { fileToBase64 } from '@/utils/converterUtils'
import type Insured from '../../common/common/models/APP/Insured'
import AddressDetails from '../../common/hiring/models/APP/AddressDetails'
import DocumentDetails from '../../common/hiring/models/APP/DocumentDetails'
import UserDetails from '../../common/hiring/models/APP/UserDetails'
import { UserRoleEnum } from '../models/enums'
import { useCommonStore } from '../stores/commonStore'
import { useHealthStore } from '../stores/healthStore'
import CommonMapper from './CommonMapper'
import HealthQuestionnaireMapper from './HealthQuestionnaireMapper'
import {
  ModifiedRaterRequest,
  ProductRaterRequest,
  SimpleRaterRequest
} from './health/RequestModels'

function _convertSections(
  acc: AbstractAccordion<any>[],
  [key, items]: [string, any[]]
): AbstractAccordion<any>[] {
  if (key === ':type') return acc

  const elements = _getAccordionElements(key, items)
  return [...acc, ...elements]
}

function _getAccordionElements(key: string, items: any[]): AbstractAccordion<any>[] {
  return (
    items?.reduce((acc, attrs: any) => {
      const element = SectionFactory.createAccordion(key, attrs)
      if (!element) return acc

      return [...acc, element]
    }, []) ?? []
  )
}

function _getDocumentDetails(data: any, file: any) {
  if (!file) return undefined
  const mimeType = file?.mimeType
    ? EXTENSION_MIMETYPE[file.mimeType.toLowerCase()]
    : 'application/pdf'
  return new DocumentDetails({
    personType: data?.personType,
    documentType: data?.documentType,
    documentNumber: data?.documentNumber,
    documentExpirationDate: data?.documentExpirationDate,
    documentId: file?.documentId || file?.documentData,
    fileName: file?.fileName,
    mimeType: mimeType
  })
}

function _getHealthInsured(data: any, role: UserRoleEnum, files?: any) {
  const additionalCoverages =
    data?.coverages?.map((c: any) => {
      return new AdditionalCoverage({
        key: c.coverageCode,
        checked: true,
        value: c.sumInsured
      })
    }) ?? []
  const fileExtra = files?.find((f: any) => f.documentType !== '1')

  const { weight, height, medicalSituation, healthSituation } =
    HealthQuestionnaireMapper.fromQuestionnaireResponse(data?.questionnaire)

  const dataProtection = data?.dataProtection

  return new HealthHiringInsured({
    riskNumber: data?.riskNumber,
    role: role,
    isFirstInsured: data?.isFirstInsured,
    anotherCompany: data?.hasPolicyInOtherCompany,
    userDetails: new UserDetails({
      birthDate: data?.birthDate,
      name: data?.name?.toUpperCase(),
      surname1: data?.firstSurname?.toUpperCase(),
      surname2: data?.secondSurname?.toUpperCase(),
      gender: data?.sex, // H / M
      email: data?.email,
      phone: data?.mobilePhone,
      prefix: data?.prefix,
      weight,
      height
    }),
    documentDetails: new DocumentDetails({
      personType: data?.personType,
      documentType: data?.documentType,
      documentNumber: data?.documentNumber,
      documentExpirationDate: data?.documentExpirationDate
    }),
    extraDocument: _getDocumentDetails(data, fileExtra),
    addressDetails: new AddressDetails({
      postalCode: data?.postalCode,
      country: data?.nationality,
      city: data?.city,
      province: data?.province,
      address: data?.street,
      streetType: data?.streetType,
      roadNumber: data?.house,
      addressExtra: data?.restAddress,
      addressNormalize: data?.addressNormalice,
      houseType: String(data?.houseType),
      gateway: data?.gateway,
      stair: data?.stair,
      floor: data?.floor,
      doorway: data?.doorway
    }),
    additionalCoverages,
    medicalSituation,
    healthSituation,
    declaration: data?.policyHolderConsent,

    // todo convert to dataProtection
    privacyPolicyConsent: dataProtection?.privacyPolicyConsent,
    // asisaNewsConsent: dataProtection?.asisaNewsConsent ?? null, //? borrar??

    thirdPartyWrittenAdvertising: dataProtection?.thirdPartyTelephoneAdvertising,
    thirdPartyTelephoneAdvertising: dataProtection?.thirdPartyWrittenAdvertising,
    dataTransfer: dataProtection?.dataTransfer
  })
}

function getCommonConfigs() {
  const commonStore = useCommonStore()
  return commonStore.getCommonConfigs()
}

function getHealthConfigs() {
  const healthStore = useHealthStore()
  const healthConfigs = healthStore.getBranchConfiguration()
  return healthConfigs
}

export default class HealthMapper {
  static #insuredAdapter(insureds: Insured[]) {
    return insureds.map((i, order) => {
      const coverages = i.additionalCoverages
        .filter((i) => i.checked)
        .map((c) => ({
          coverageCode: c.key,
          sumInsured: c.value ? String(c.value) : null
        }))

      const optionalCoverages = coverages.length ? coverages : undefined

      return {
        postalCode: i.addressDetails.postalCode,
        birthDate: CommonMapper.toMiddlewareDate(i.userDetails.birthDate),
        riskNumber: String(order + 1),
        optionalCoverage: optionalCoverages
      }
    })
  }

  // ❔ NUEVA TARIFICACION
  static toRaterRequest(data: HealthRaterAnswers) {
    const holder = data.insureds.find((i) => i.isHolder)
    const defaultPostalCode = getCommonConfigs()?.defaultPostalCode ?? null
    const policyHolderPostal = holder?.addressDetails.postalCode || defaultPostalCode

    return new SimpleRaterRequest({
      businessLine: data.businessLine,
      insuranceStartDate: CommonMapper.toMiddlewareDate(data.startDate),
      insuranceType: data.insuranceType,
      stayAbroadType: data.abroadOption,
      policyHolderPostal: policyHolderPostal,
      insureds: HealthMapper.#insuredAdapter(data.insureds)
    })
  }

  // ❔ Tarificar producto especifico / calcular precio de retarificacion de producto en contratacion
  static toProductRaterRequest(data: HealthRaterAnswers, isQuoted: boolean = false) {
    const clientProduct = [
      {
        clientCode: data.product?.clientCode ?? null,
        productCode: data.product?.productCode ?? null
      }
    ]
    const commonConfig = getCommonConfigs()
    const defaultPostalCode = commonConfig?.defaultPostalCode ?? null
    const policyHolderPostal = data.holder.addressDetails.postalCode || defaultPostalCode

    return new ProductRaterRequest({
      clientProduct,
      businessLine: data.businessLine,
      insuranceStartDate: CommonMapper.toMiddlewareDate(data.startDate),
      insuranceType: data.insuranceType,
      stayAbroadType: data.abroadOption,
      policyHolderPostal: policyHolderPostal,
      insureds: HealthMapper.#insuredAdapter(data.insureds),
      isQuote: isQuoted
    })
  }

  // ❔ Consolidar retarificacion
  static toReRaterRequest(data: HealthRaterAnswers, isQuote: boolean = false) {
    const commonConfig = getCommonConfigs()
    const defaultPostalCode = commonConfig?.defaultPostalCode ?? null
    const policyHolderPostal = data.holder.addressDetails.postalCode || defaultPostalCode

    return new ModifiedRaterRequest({
      quotationId: data.quotationId,
      quotationNumber: data.product?.quotationNumber ?? null,
      businessLine: data.businessLine,
      insuranceStartDate: CommonMapper.toMiddlewareDate(data.startDate),
      insuranceType: data.insuranceType,
      stayAbroadType: data.abroadOption,
      policyHolderPostal: policyHolderPostal,
      insureds: HealthMapper.#insuredAdapter(data.insureds),
      isQuote
    })
  }

  static fromModifiedRaterResponse(data: any): HealthInsurance {
    if (!data) return new HealthInsurance({})
    const branchConfig = getHealthConfigs()
    const highlights = CommonMapper.fromDXPProductHighlights(data.highlights)
    const prices = CommonMapper.fromDXPProductPrices(data.rates)
    const totalPrices = CommonMapper.fromDXPProductPricesTotal(data.rates)

    const checkItems = ({ text, value: check }: any) => ({ text, check })
    const coverages = SectionFactory.createAccordion('accordionChecks', {
      id: 'coverages',
      items: data.coverages?.map(checkItems) ?? []
    })

    const optionalCoverages = SectionFactory.createAccordion('accordionChecks', {
      id: 'optional-coverages',
      items: data.optionalCoverages?.map(checkItems) ?? []
    })

    const simpleItems = ({ text, value }: any) => ({ text, value })
    const coPayments = SectionFactory.createAccordion('accordionSimples', {
      id: 'co-payments',
      items: data.copayments?.map(simpleItems) ?? []
    })
    const deficiencies = SectionFactory.createAccordion('accordionSimples', {
      id: 'deficiencies',
      items: data.deficiencies?.map(simpleItems) ?? []
    })

    const sectionEntries = Object.entries((data.sections as Record<string, any[]>) ?? {})
    const sections: AbstractAccordion<any>[] = sectionEntries.reduce(_convertSections, [])
    sections.sort((a, b) => Number(a.order) - Number(b.order))

    return new HealthInsurance({
      quotationNumber: data.quotationNumber,
      clientCode: data.clientCode,
      productCode: data.productId,
      productName: data?.productName,
      businessLine: data.businessLine,
      image: import.meta.env.VITE_API_URL_DXP_ROOT + data?.imagePath,
      group: data.family,
      order: data.order,
      title: data.productName,
      description: data.productDescription,
      type: data.contractType,
      copayType: data.copaymentType,
      copayText:
        branchConfig?.copaymentTypesOptions.find((i) => i.value === data.copaymentType)?.label ??
        null,
      contractType: data.contractType,
      highlights,
      deficiencies,
      coverages,
      optionalCoverages,
      coPayments,
      sections,
      prices,
      totalPrices
    })
  }

  static toSaveQuotation(
    quotationId: string,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    return {
      quotationId: quotationId,
      hiring: {
        quotationNumber: product.quotationNumber,
        insuranceStartDate: CommonMapper.toMiddlewareDate(startDate),
        businessLine: product.businessLine,
        clientCode: product.clientCode,
        productCode: product.productCode,
        billingCycle: CommonMapper.toDXPProductPrice(billingCycle),
        role: holderRole
      }
    }
  }

  static fromRecoverHiringData(data: any, product: any): HealthHiringAnswers {
    const holderDocuments = data.documents.filter((d: any) => d.riskNumber === 0)
    const holder = _getHealthInsured(data.policyHolder, data.policyHolder.role, holderDocuments)
    if (!holder.userDetails.email) {
      holder.userDetails.email = data.hiring.recoveryMail ?? null
    }

    let isFirstOne = true
    const insureds = data.insureds.map((insured: any) => {
      const isHolder =
        data.policyHolder.role === UserRoleEnum.HOLDER_INSURED && insured.riskNumber === 1
      const documents = data.documents.filter((d: any) => d.riskNumber === insured.riskNumber)
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
      const isFirstInsured = !isHolder && isFirstOne
      if (isFirstInsured) {
        isFirstOne = false
      }

      if (data.coverageCode) {
        insured.coverages = insured.coverages.filter(
          (coverage: any) => coverage.coverageCode !== data.coverageCode
        )
      }

      return new HealthHiringInsured(
        _getHealthInsured({ ...insured, isFirstInsured }, role, documents)
      )
    })

    if (holder.role === UserRoleEnum.HOLDER_INSURED) {
      const preContractualDocumentChecked = holder.preContractualDocumentChecked
      const thirdPartiesAnswered = holder.thirdPartiesAnswered
      const thirdPartyTelephoneAdvertising = holder.thirdPartyTelephoneAdvertising
      const thirdPartyWrittenAdvertising = holder.thirdPartyWrittenAdvertising
      const privacyPolicyConsent = holder.privacyPolicyConsent
      const dataTransfer = holder.dataTransfer

      insureds[0].updateAttributes({
        preContractualDocumentChecked,
        thirdPartiesAnswered,
        thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising,
        privacyPolicyConsent,
        dataTransfer,
        userDetails: { email: holder.userDetails.email }
      })
      holder.updateAttributes(insureds[0])
    }

    const afterCoverages = data.hiring.stepCode > HealthInsuranceProcessStepEnum.COVERAGES
    const hasInsuredWithCoverages = data.insureds.some((i: any) => !!i.coverages?.length)
    const betterAssurance =
      hasInsuredWithCoverages || afterCoverages ? hasInsuredWithCoverages : null

    return new HealthHiringAnswers({
      quotationId: data.quotationId,
      businessLine: data.hiring.businessLine,
      billingCycle: CommonMapper.fromDXPProductPrice(data.hiring.billingCycle),
      price: data.hiring.price,
      totalPrice: data.hiring.totalPrice,
      coverageCode: data.coverageCode,
      product: new HealthInsurance({
        ...product,
        insuranceType:
          data.hiring.insuranceTypes.length > 0 ? data.hiring.insuranceTypes[0].toString() : null,
        abroadOption: data.hiring.stayAbroadType.toString(),
        quotationNumber: data.hiring.quotationNumber,
        clientCode: data.hiring.clientCode,
        productCode: data.hiring.productCode
      }),
      processStatus: new ProcessStatus({
        statusCode: data.hiring.statusCode,
        stepCode: data.hiring.stepCode
      }),
      startDate: CommonMapper.fromMiddlewareDate(data.hiring.insuranceStartDate),
      bankDetails: new BankDetails({
        name: data.paymentMeans?.holderName,
        iban: data.paymentMeans?.iban
      }),
      betterAssurance,
      holder,
      insureds
    })
  }

  static fromAdditionalCoveragesConfig(data: any): AdditionalCoverageConfig {
    if (!data) return new AdditionalCoverageConfig({})
    const highlights = CommonMapper.fromDXPProductHighlights(data.highlights)
    const sectionEntries = Object.entries((data.sections as Record<string, any[]>) ?? {})
    const sections: AbstractAccordion<any>[] = sectionEntries.reduce(_convertSections, [])
    sections.sort((a, b) => Number(a.order) - Number(b.order))

    return new AdditionalCoverageConfig({
      code: data.coverageCode,
      number: data.coverageNumber,
      mandatory: data.mandatory,
      minAge: data.minAge,
      maxAge: data.maxAge,
      capitals: data.coverageInfoCapitals.map((i: any) => i.sumInsured),
      period: data.priceSuffix,
      label: data.coverageName,
      headerTitle: data.coveragePreTitle,
      icon: import.meta.env.VITE_API_URL_DXP_ROOT + data.coverageIcon,
      message: data.coverageDescription,
      highlights,
      sections
    })
  }

  static async toInsuredDocumentRequest(quotationId: string, insured: HealthHiringInsured) {
    const file = insured.extraDocument?.file
    const documentObject: {
      riskNumber: number
      documentType?: string
      fileName?: string
      documentData?: string
    } = { riskNumber: insured.riskNumber }

    if (file) {
      const documentData = await fileToBase64(file)
      documentObject.fileName = insured.extraDocument?.fileName!
      documentObject.documentType = insured.extraDocument?.documentType
      documentObject.documentData = documentData
    }
    return {
      quotationId,
      document: [documentObject],
      documentNumber: insured.documentDetails.documentNumber
    }
  }

  static async toHolderDocumentRequest(quotationId: string, holder: HealthHiringInsured) {
    const documentExpirationDate = holder.documentDetails.documentExpirationDate
    const document = []

    if (holder.extraDocument && holder.extraDocument.file) {
      const extraFile = holder.extraDocument.file
      const extraDocumentData = extraFile ? await fileToBase64(extraFile) : null
      document.push({
        fileName: holder.extraDocument.fileName,
        documentType: holder.extraDocument.documentType,
        documentData: extraDocumentData,
        riskNumber: holder.riskNumber
      })
    }

    return {
      quotationId,
      document,
      policyHolder: {
        role: holder.role,
        nacionality: holder.addressDetails.country,
        personType: holder.documentDetails.personType,
        documentType: holder.documentDetails.documentType,
        documentNumber: holder.documentDetails.documentNumber,
        documentExpirationDate: documentExpirationDate
          ? CommonMapper.toMiddlewareDate(documentExpirationDate)
          : undefined,
        hasPolicyInOtherCompany: holder.anotherCompany ?? false, // Para el caso de TN el valor es false porque no se muestra la pantalla y el campo es obligatorio
        privacyPolicyConsent: holder.privacyPolicyConsent,
        //asisaNewsConsent: true,
        birthDate: CommonMapper.toMiddlewareDate(holder.userDetails.birthDate)
      }
    }
  }

  static toHolderRequest(quotationId: string, holder: HealthHiringInsured) {
    const dataProtection = {
      dataTransfer: holder.dataTransfer,
      thirdPartyTelephoneAdvertising: holder.thirdPartyTelephoneAdvertising,
      thirdPartyWrittenAdvertising: holder.thirdPartyWrittenAdvertising
    }
    return {
      quotationId,
      policyHolder: {
        role: holder.role,
        name: holder.userDetails.name?.toUpperCase(),
        firstSurname: holder.userDetails.surname1?.toUpperCase(),
        secondSurname: holder.userDetails.surname2?.toUpperCase(),
        birthDate: CommonMapper.toMiddlewareDate(holder.userDetails.birthDate),
        sex: holder.userDetails.gender,
        street: holder.addressDetails.address,
        city: holder.addressDetails.city,
        house: holder.addressDetails.roadNumber,
        postalCode: holder.addressDetails.postalCode,
        province: holder.addressDetails.province,
        houseType: holder.addressDetails.houseType,
        gateway: holder.addressDetails.gateway,
        stair: holder.addressDetails.stair,
        floor: holder.addressDetails.floor,
        doorway: holder.addressDetails.doorway,
        restAddres: holder.addressDetails.addressExtra,
        addressNormalice: holder.addressDetails.addressNormalize,
        streetType: holder.addressDetails.streetType,
        email: holder.userDetails.email,
        prefix: holder.userDetails.prefix,
        mobilePhone: holder.userDetails.phone
      },
      dataProtection: !holder.thirdPartiesAnswered ? dataProtection : undefined
    }
  }

  static toInsuredsRequest(quotationId: string, insureds: HealthHiringInsured[]) {
    const insuredToSave: any[] = insureds.map((insured) => ({
      hasPolicyInOtherCompany: insured.anotherCompany,
      riskNumber: insured.riskNumber,
      nacionality: insured.addressDetails.country,
      documentType: insured.documentDetails.documentType,
      documentNumber: insured.documentDetails.documentNumber,
      documentExpirationDate: insured.documentDetails.documentExpirationDate
        ? CommonMapper.toMiddlewareDate(insured.documentDetails.documentExpirationDate)
        : undefined,
      name: insured.userDetails.name?.toUpperCase(),
      firstSurname: insured.userDetails.surname1?.toUpperCase(),
      secondSurname: insured.userDetails.surname2?.toUpperCase(),
      birthDate: CommonMapper.toMiddlewareDate(insured.userDetails.birthDate),
      sex: insured.userDetails.gender,
      streetType: insured.addressDetails.streetType,
      street: insured.addressDetails.address,
      city: insured.addressDetails.city,
      house: insured.addressDetails.roadNumber,
      postalCode: insured.addressDetails.postalCode,
      province: insured.addressDetails.province,
      houseType: insured.addressDetails.houseType,
      gateway: insured.addressDetails.gateway,
      stair: insured.addressDetails.stair,
      floor: insured.addressDetails.floor,
      doorway: insured.addressDetails.doorway,
      restAddres: insured.addressDetails.addressExtra,
      addressNormalice: insured.addressDetails.addressNormalize,
      email: insured.userDetails.email,
      prefix: insured.userDetails.prefix,
      mobilePhone: insured.userDetails.phone,
      policyHolderConsent: insured.declaration
    }))

    return {
      quotationId,
      insureds: insuredToSave
    }
  }

  static toQuestionnaireRequest(
    quotationId: string,
    insureds: HealthHiringInsured[] | ForeignHealthHiringInsured[]
  ) {
    const questionnaires = insureds.map((insured) => ({
      riskNumber: insured.riskNumber,
      questionaries: HealthQuestionnaireMapper.toInsuredQuestionnaire(
        insured as HealthHiringInsured
      )
    }))

    return {
      title: 'ASISASALUD',
      quotationId,
      questionaries: questionnaires
    }
  }

  static fromLandingResponse(data: any) {
    return data.insureds.map(
      (insured: any) =>
        new HealthLandingInsured({
          birthDate: CommonMapper.fromMiddlewareDate(insured.birthDate),
          firstSurname: insured.firstSurname,
          name: insured.name,
          riskNumber: insured.riskNumber,
          secondSurname: insured.secondSurname
        })
    )
  }

  static async toLandingRequest(insureds: HealthLandingInsured[]) {
    const getExtension = (file: File) => file.name.split('.')[1]
    const getType = (file: File) => file.type.split('/')[1]

    return {
      insureds: insureds.map((insured) => ({
        name: insured.name,
        firstSurname: insured.firstSurname,
        secondSurname: insured.secondSurname,
        riskNumber: insured.riskNumber,
        insuranceName: insured.insuranceName,
        policynumber: insured.policynumber,
        documents: [
          {
            fileName: `tarjeta_sanitaria.${getExtension(insured.healthCard[0])}`,
            documentType: getType(insured.healthCard[0]),
            documentData: insured.healthCardBase64
          },
          {
            fileName: `recibo_asistencia.${getExtension(insured.lastReceipt[0])}`,
            documentType: getType(insured.lastReceipt[0]),
            documentData: insured.lastReceiptBase64
          }
        ]
      }))
    }
  }

  static toExistClientRequest(holder: HealthHiringInsured) {
    return new ExistClientRequest({
      firstSurname: (holder.userDetails.surname1 ?? '')?.toUpperCase(),
      secondSurname: (holder.userDetails.surname2 ?? '')?.toUpperCase(),
      name: (holder.userDetails.name ?? '')?.toUpperCase(),
      sex: holder.userDetails.gender,
      documentType: holder.documentDetails.documentType,
      documentNumber: holder.documentDetails.documentNumber,
      birthDay: CommonMapper.toMiddlewareDate(holder.userDetails.birthDate)
    })
  }
}
